import employeeService from '../services/employeeService';
import leaveService from '../services/leaveService';
import attendanceService from '../services/attendanceService';
import payrollService from '../services/payrollService';
import employeeDeploymentService from '../services/employeeDeploymentService';
import apiClient from '../lib/apiClient';
import { computeAttendanceStats, monthDateRange } from './attendanceHelpers';

const LEAVE_QUOTAS = { casual: 12, sick: 12, earned: 15, paid: 15, unpaid: 365 };

export const buildLeaveBalance = (leavesList, year = new Date().getFullYear()) => {
  const used = {};
  const pending = {};
  (leavesList || []).forEach((row) => {
    const leaveYear = row.leave_date ? new Date(row.leave_date).getFullYear() : year;
    if (leaveYear !== year) return;
    const type = row.leave_type || 'unpaid';
    const days = Number(row.total_days) || 1;
    if (row.status === 'approved') used[type] = (used[type] || 0) + days;
    else if (row.status === 'pending') pending[type] = (pending[type] || 0) + days;
  });
  return Object.entries(LEAVE_QUOTAS).map(([leave_type, quota]) => ({
    leave_type,
    quota,
    used: used[leave_type] || 0,
    pending: pending[leave_type] || 0,
    available: Math.max(0, quota - (used[leave_type] || 0) - (pending[leave_type] || 0)),
  }));
};

export const resolveEmployeeByEmail = async (email) => {
  if (!email) return null;
  const result = await employeeService.getEmployees({ search: email });
  if (!result.success || !result.data?.length) return null;
  const emailLower = email.toLowerCase().trim();
  const exact = result.data.find((e) => e.email?.toLowerCase().trim() === emailLower);
  return exact || result.data[0];
};

/** Prefer user_id link (same as backend ESS), then exact email */
export const resolveEmployee = async (userProfile) => {
  if (!userProfile) return null;

  if (userProfile.id) {
    const all = await employeeService.getEmployees();
    if (all.success && all.data?.length) {
      const linked = all.data.find((e) => e.user_id === userProfile.id);
      if (linked) return linked;
    }
  }

  if (userProfile.email) {
    const byEmail = await resolveEmployeeByEmail(userProfile.email);
    if (byEmail) {
      if (!userProfile.id || !byEmail.user_id || byEmail.user_id === userProfile.id) {
        return byEmail;
      }
    }
  }

  return null;
};

const loadDeployment = async (employee) => {
  if (employee.current_deployment_id) {
    const dep = await employeeDeploymentService.getDeployment(employee.current_deployment_id);
    if (dep.success && dep.data) {
      return {
        client_name: dep.data.client_name,
        site_name: dep.data.site_name,
        shift_name: dep.data.shift_name,
        shift_start: dep.data.shift_start || dep.data.start_time,
        shift_end: dep.data.shift_end || dep.data.end_time,
      };
    }
  }
  const list = await employeeDeploymentService.getDeployments({
    employee_id: employee.id,
    status: 'active',
  });
  if (list.success && list.data?.length) {
    const d = list.data[0];
    return {
      client_name: d.client_name,
      site_name: d.site_name,
      shift_name: d.shift_name,
      shift_start: d.shift_start || d.start_time,
      shift_end: d.shift_end || d.end_time,
    };
  }
  return null;
};

const loadLetters = async (employeeId) => {
  try {
    const result = await apiClient.get('/offer-letters', { employee_id: employeeId });
    if (result.success) {
      return {
        offer_letters: (result.data || []).map((l) => ({
          ...l,
          file_path: l.file_path || null,
          download_url: `/offer-letters/${l.id}/download`,
        })),
        increment_letters: [],
      };
    }
  } catch (e) {
    /* offer letters optional */
  }
  return { offer_letters: [], increment_letters: [] };
};

export const loadClientSidePortal = async (userProfile) => {
  const employee = await resolveEmployee(userProfile);
  if (!employee) {
    return { success: false, error: 'Employee record not found for your login. Contact HR.' };
  }

  const employeeId = employee.id;
  const { start, end } = monthDateRange();
  const year = new Date().getFullYear();

  const [leavesRes, payslipRes, attendanceRes, todayRes, deployment, holidaysRes, docsRes, letters] =
    await Promise.all([
      leaveService.getLeaves({ employee_id: employeeId }),
      payrollService.getPayrollRecords({ employee_id: employeeId }),
      attendanceService.getAttendanceRecords({
        employee_id: employeeId,
        date_from: start,
        date_to: end,
      }),
      attendanceService.getTodayStatus(employeeId),
      loadDeployment(employee),
      apiClient.get('/holidays', { year }),
      employeeService.getEmployeeDocuments(employeeId),
      loadLetters(employeeId),
    ]);

  const leaves = leavesRes.success ? leavesRes.data || [] : [];
  const attendanceRecords = attendanceRes.success ? attendanceRes.data || [] : [];
  const attendanceStats = computeAttendanceStats(attendanceRecords);
  const leaveBalance = buildLeaveBalance(leaves, year);

  return {
    success: true,
    data: {
      employee,
      deployment,
      dashboard: { employee, deployment, attendance_stats: attendanceStats, leave_balance: leaveBalance },
      leaves,
      payslips: payslipRes.success ? payslipRes.data || [] : [],
      attendanceRecords,
      attendanceStats,
      todayStatus: todayRes.success ? todayRes.data : null,
      leaveBalance,
      holidays: holidaysRes.success ? holidaysRes.data || [] : [],
      documents: docsRes.success ? docsRes.data || [] : [],
      letters,
      notificationCenter: [],
      clientSideMode: true,
    },
  };
};

export const fetchEmployeeLeaves = async (employeeId) => {
  const year = new Date().getFullYear();
  const result = await leaveService.getLeaves({ employee_id: employeeId });
  const leaves = result.success ? result.data || [] : [];
  return { leaves, leaveBalance: buildLeaveBalance(leaves, year) };
};

export const fetchEmployeeDocuments = async (employeeId) => {
  const [docsRes, letters] = await Promise.all([
    employeeService.getEmployeeDocuments(employeeId),
    loadLetters(employeeId),
  ]);
  return {
    documents: docsRes.success ? docsRes.data || [] : [],
    letters,
  };
};

export const fetchEmployeeAttendance = async (employeeId, dateFrom, dateTo) => {
  const [recordsRes, summaryRes] = await Promise.all([
    attendanceService.getAttendanceRecords({
      employee_id: employeeId,
      date_from: dateFrom,
      date_to: dateTo,
    }),
    attendanceService.getAttendanceSummary(employeeId, dateFrom, dateTo),
  ]);
  const records = recordsRes.success ? recordsRes.data || [] : [];
  return {
    records,
    stats: computeAttendanceStats(records),
    summary: summaryRes.success ? summaryRes.data : null,
  };
};

export const updateEmployeeProfileClient = async (employeeId, form) =>
  employeeService.updateEmployee(employeeId, {
    phone: form.phone,
    address: form.address,
    emergency_contact: form.emergency_contact,
    bank_name: form.bank_name,
    bank_account: form.bank_account,
    ifsc_code: form.ifsc_code,
  });

export const ESS_CLIENT_API_KEY = 'ess_use_client_apis';
